import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Lock, User, Eye, EyeOff, Shield, AlertCircle } from 'lucide-react';
import Logo from '../../components/Logo';

const SecureAdminLogin = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // Check if security clearance exists and is valid (within 30 minutes)
    const clearance = sessionStorage.getItem('security_clearance');
    const timestamp = sessionStorage.getItem('clearance_timestamp');
    
    if (!clearance || clearance !== 'granted') {
      navigate('/secure-admin-access');
      return;
    }

    // Check if clearance has expired (30 minutes)
    if (timestamp) {
      const elapsed = Date.now() - parseInt(timestamp);
      const thirtyMinutes = 30 * 60 * 1000;
      
      if (elapsed > thirtyMinutes) {
        sessionStorage.removeItem('security_clearance');
        sessionStorage.removeItem('clearance_timestamp');
        alert('Security clearance expired. Please verify again.');
        navigate('/secure-admin-access');
      }
    }
  }, [navigate]);

  const handleLogin = async (e) => {
    e.preventDefault();
    
    if (!email || !password) {
      alert('Please enter both email and password');
      return;
    }

    setLoading(true);

    try {
      const BACKEND_URL = '';
      
      const response = await fetch(`${BACKEND_URL}/api/admin/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          username: email,
          password: password
        })
      });

      const data = await response.json();

      if (response.ok && data.token) {
        // Store admin token with security flag
        localStorage.setItem('secure_admin_token', data.token);
        localStorage.setItem('secure_admin_user', JSON.stringify(data.admin));
        localStorage.setItem('admin_session_start', Date.now().toString());
        
        alert('Secure admin login successful');
        
        // Redirect to secure admin dashboard
        navigate('/secure-admin-access/dashboard');
      } else {
        alert('Login Failed: ' + (data.detail || 'Invalid credentials'));
      }
    } catch (error) {
      console.error('Admin login error:', error);
      alert('Failed to connect to server');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-blue-950 to-slate-950 flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        {/* Security Clearance Badge */}
        <div className="mb-6 p-4 bg-green-900/20 border border-green-500/50 rounded-xl">
          <div className="flex items-center justify-center gap-2">
            <Shield className="w-5 h-5 text-green-400" />
            <p className="text-green-400 font-semibold text-sm">
              ✓ Security Clearance Verified
            </p>
          </div>
        </div>

        {/* Logo */}
        <div className="flex justify-center mb-8">
          <Logo size="large" showText={true} />
        </div>

        {/* Login Card */}
        <div className="bg-gradient-to-br from-slate-900 to-slate-800 border-2 border-cyan-500/30 rounded-2xl p-8 shadow-2xl">
          <div className="text-center mb-8">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-br from-cyan-500 to-blue-500 flex items-center justify-center">
              <Lock className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-3xl font-bold text-white mb-2">Secure Admin Portal</h1>
            <p className="text-cyan-400">Administrator Access</p>
          </div>

          <form onSubmit={handleLogin} className="space-y-6">
            {/* Email Input */}
            <div>
              <label className="block text-cyan-400 text-sm font-semibold mb-2">
                Admin Email
              </label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-cyan-600" />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="admin@edgetrade.com"
                  className="w-full pl-10 pr-4 py-3 bg-slate-800 border border-cyan-500/30 rounded-lg text-white placeholder-cyan-800 focus:outline-none focus:border-cyan-400 transition-colors"
                />
              </div>
            </div>

            {/* Password Input */}
            <div>
              <label className="block text-cyan-400 text-sm font-semibold mb-2">
                Password
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-cyan-600" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter admin password"
                  className="w-full pl-10 pr-12 py-3 bg-slate-800 border border-cyan-500/30 rounded-lg text-white placeholder-cyan-800 focus:outline-none focus:border-cyan-400 transition-colors"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-cyan-600 hover:text-cyan-400"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            {/* Login Button */}
            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 rounded-lg bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-400 hover:to-blue-400 text-white font-bold text-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <div className="flex items-center justify-center gap-2">
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  Authenticating...
                </div>
              ) : (
                'Access Secure Panel'
              )}
            </button>
          </form>

          {/* Security Notice */}
          <div className="mt-6 p-4 bg-red-900/20 border border-red-500/30 rounded-lg">
            <div className="flex items-start gap-2">
              <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
              <p className="text-red-400 text-xs">
                This is a restricted area. All access attempts are monitored and logged. 
                Unauthorized access may result in legal action.
              </p>
            </div>
          </div>
        </div>

        {/* Session Info */}
        <div className="text-center mt-6">
          <p className="text-cyan-600 text-xs">
            Secure session active | Auto-logout in 30 minutes
          </p>
        </div>
      </div>
    </div>
  );
};

export default SecureAdminLogin;
